import { AuthenticationResult } from '@azure/msal-browser';
import { createAction, props } from '@ngrx/store';
import { ApproveStatus, WarningType } from '@shared/enums/dpr';
import { UserSession } from '@shared/models';
import {
  Attachment,
  DprActivity,
  DprApprover,
  DprConsumable,
  DprDetails,
  DprDetailsSync,
  DprHidden,
  DprForecastActivity,
  DprHse,
  DprOnlineApproval,
  DprPersonnel,
  DprProgress,
  DprSummary,
  DprWeather,
  DprAttachment,
  DprStatusTracker,
  DprTableOfColumns,
  DprActivitiesDurationHistory,
  DprProgressWithoutTarget,
  DprFreeTextField,
} from '@shared/models/dpr';

export const setDprDetailsList = createAction(
  '[Offline] Set Dpr Details List',
  props<{ dprDetailsList: DprDetails[] }>()
);

export const setDprDetails = createAction(
  '[Offline] Set Dpr Details',
  props<{ dprDetails: DprDetails }>()
);

export const updateDprDetails = createAction(
  '[Offline] Update Dpr Details',
  props<{ oldDprDetailId: number | string; dprDetails: DprDetails }>()
);

export const updateExistingDprDetails = createAction(
  '[Offline] Update Existing Dpr Details',
  props<{ dprDetails: DprDetails }>()
);

export const setBackupUserDetails = createAction(
  '[Offline] Set Backup User Details',
  props<{ backupUserDetails: UserSession }>()
);

export const setBackupLastDprs = createAction(
  '[Offline] Set Backup Last Dprs',
  props<{ backupLastDprs: DprDetails[] }>()
);

export const setBackupAuthenticationResult = createAction(
  '[Offline] Set Backup Authentication Result',
  props<{ backupAuthenticationResult: AuthenticationResult }>()
);

export const addDprDetails = createAction(
  '[Offline] Add Dpr Details',
  props<{ dprDetails: DprDetails }>()
);

export const setDprDetailsOfflineChanges = createAction(
  '[Offline] Set Dpr Details Offline Changes',
  props<{ dprDetailsOfflineChanges: DprDetailsSync[] }>()
);

export const updateDprDetailsOfflineChanges = createAction(
  '[Offline] Update Dpr Details Offline Changes',
  props<{ changes: DprDetailsSync }>()
);

export const setDprDetailsSignedReport = createAction(
  '[Offline] Set Dpr Details Signed Report',
  props<{ dprDetailId: number | string; attachment: Attachment }>()
);

export const setDprDetailsLockedDate = createAction(
  '[Offline] Set Dpr Details Locked Date',
  props<{ dprDetailId: number | string; lockedDate: Date }>()
);

export const setDprDetailsNewRevision = createAction(
  '[Offline] Set Dpr Details New Revision',
  props<{ dprDetailId: number | string; dprDetails: DprDetails }>()
);

export const setDprActivitiesHistory = createAction(
  '[Offline] Set Dpr Details Activities History',
  props<{
    dprDetailId: number | string;
    dprActivitiesDurationHistory: DprActivitiesDurationHistory[];
  }>()
);

export const setDprActivities = createAction(
  '[Offline] Set Dpr Activities',
  props<{
    dprActivities: DprActivity[];
    dprParallelActivities: DprActivity[];
    firstWarning: WarningType;
    warningStatusTab: string;
    dprDetailId: number | string;
  }>()
);

export const setDprStatusTracker = createAction(
  '[Offline] Set Dpr Status Tracker',
  props<{
    dprStatusTracker: DprStatusTracker;
    dprDetailId: number | string;
  }>()
);

export const setDprWeather = createAction(
  '[Offline] Set Dpr Weather',
  props<{
    dprWeather: DprWeather;
    dprDetailId: number | string;
  }>()
);

export const setDprHse = createAction(
  '[Offline] Set Dpr Hse',
  props<{
    dprHse: DprHse;
    dprDetailId: number | string;
  }>()
);

export const setDprPersonnel = createAction(
  '[Offline] Set Dpr Personnel',
  props<{
    dprPersonnel: DprPersonnel;
    dprDetailId: number | string;
  }>()
);

export const setDprConsumable = createAction(
  '[Offline] Set Dpr Consumable',
  props<{
    dprConsumable: DprConsumable;
    dprDetailId: number | string;
  }>()
);

export const setDprSummary = createAction(
  '[Offline] Set Dpr Summary',
  props<{
    dprSummary: DprSummary;
    firstWarning: WarningType;
    warningStatusTab: string;
    dprDetailId: number | string;
  }>()
);

export const setDprForecastActivities = createAction(
  '[Offline] Set Dpr Forecast Activities',
  props<{
    dprForecastActivities: DprForecastActivity[];
    firstWarning: WarningType;
    warningStatusTab: string;
    dprDetailId: number | string;
  }>()
);

export const setDprSummaryExternalComment = createAction(
  '[Offline] Set Dpr Summary External Comment',
  props<{
    dprSummary: DprSummary;
    firstWarning: WarningType;
    warningStatusTab: string;
    dprDetailId: number | string;
  }>()
);

export const setDprFreeTextField = createAction(
  '[Offline] Set Dpr Free Text Field',
  props<{
    dprFreeTextField: DprFreeTextField;
    dprDetailId: number | string;
  }>()
);

export const setDprProgress = createAction(
  '[Offline] Set Dpr Progress',
  props<{
    dprProgress: DprProgress;
    dprDetailId: number | string;
  }>()
);

export const setDprProgressWithoutTarget = createAction(
  '[Offline] Set Dpr Progress without Target',
  props<{
    dprProgressWithoutTarget: DprProgressWithoutTarget;
    dprDetailId: number | string;
  }>()
);

export const setDprTableOfColumns = createAction(
  '[Offline] Set Dpr Table Of Column',
  props<{
    dprTableOfColumn: DprTableOfColumns;
    dprDetailId: number | string;
  }>()
);

export const setDprAppendix = createAction(
  '[Offline] Set Dpr Appendix',
  props<{
    dprAppendixAttachments: DprAttachment[];
    firstWarning: WarningType;
    warningStatusTab: string;
    dprDetailId: number | string;
  }>()
);

export const setDprHidden = createAction(
  '[Offline] Set Dpr Hidden',
  props<{
    dprHidden: DprHidden[];
    firstWarning: WarningType;
    warningStatusTab: string;
    dprDetailId: number | string;
  }>()
);

export const addDprApprover = createAction(
  '[Offline] Add Dpr Approver',
  props<{ dprDetailId: number | string; dprApprover: DprApprover }>()
);

export const updateDprApprover = createAction(
  '[Offline] Update Dpr Approver',
  props<{ dprDetailId: number | string; dprApprover: DprApprover }>()
);

export const deleteDprApprover = createAction(
  '[Offline] Delete Dpr Approver',
  props<{ dprDetailId: number | string; dprApproverId: number | string }>()
);

export const updateDprOnlineApproval = createAction(
  '[Offline] Update Dpr Online Approval',
  props<{
    dprDetailId: number | string;
    dprOnlineApproval: DprOnlineApproval;
  }>()
);

export const updateDprApproveStatus = createAction(
  '[Offline] Update Dpr Approve Status',
  props<{
    dprDetailId: number | string;
    approveStatus: ApproveStatus;
  }>()
);

export const setSyncOfflineGuid = createAction(
  '[Offline] Set Sync Offline Guid'
);

export const deleteSyncOfflineGuid = createAction(
  '[Offline] Delete Sync Offline Guid'
);
